import {AuthSessionService} from '../../shared/auth/auth-session.service';
import {Component, computed, DestroyRef, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {ActivatedRoute, NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet} from '@angular/router';
import {catchError, filter, finalize, forkJoin, map, Observable, of, take} from 'rxjs';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {OidcSecurityService} from 'angular-auth-oidc-client';
import {NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle} from '@ng-bootstrap/ng-bootstrap';
import {environment} from '../../../environments/environment';
import {AuthService} from '../../shared/services/auth.service';
import {CurrentUserContextService} from '../../shared/services/current-user-context.service';
import {NotificationService} from '../../shared/services/notification.service';
import {ChatPresenceService} from '../../shared/services/chat-presence.service';
import {RoleInvitationService} from '../role-invitations/role-invitation.service';
import {NotificationBellComponent} from '../../shared/commons/notification-bell/notification-bell.component';
import {ActingAsBannerComponent} from '../../shared/commons/acting-as-banner/acting-as-banner.component';
import {ToastsContainer} from '../../shared/commons/toast/toast-container.component';
import {BreadcrumbComponent} from '../../shared/commons/breadcrumb/breadcrumb.component';
import {LoadingComponent} from '../../shared/commons/loading/loading.component';
import {AdminLayoutMobileComponent} from './admin-layout-mobile/admin-layout-mobile';
import {AccountNotProvisionedComponent, isAccountNotProvisioned} from '../../shared/commons/account-not-provisioned/account-not-provisioned.component';
import {UserRole} from '../../../models/generated-models';
import {TradeStore} from '../trade/trade.store';
import {formatBusinessTypeSummary, formatUserRoleSummary, hasSidebarUnlockedRole, MENU_ROLE_SETS, normalizeUserRoles, STAFF_ROLES, userRoleLabel, UserRoleArray} from '../../app.constants';

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'sidebarCollapsed';

function readSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

@Component({
  selector: 'app-admin-layout',
  standalone: true,
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    NgbDropdown,
    NgbDropdownToggle,
    NgbDropdownMenu,
    NgbDropdownItem,
    ToastsContainer,
    BreadcrumbComponent,
    LoadingComponent,
    AdminLayoutMobileComponent,
    AccountNotProvisionedComponent,
    NotificationBellComponent,
    ActingAsBannerComponent,
  ],
  templateUrl: './admin-layout.html',
  styleUrl: './admin-layout.scss',
})
export class AdminLayout implements OnInit, OnDestroy {
  private readonly authSession = inject(AuthSessionService);
  /** Header badges poll interval while the tab is visible. */
  private static readonly HEADER_REFRESH_INTERVAL_MS = 30_000;
  /** Slower fallback poll while the tab is hidden (WS presence keeps the user online meanwhile). */
  private static readonly HEADER_REFRESH_INTERVAL_HIDDEN_MS = 90_000;
  /** Desktop sidebar folded to icons only; remembered per browser so it survives reload/navigation. */
  protected readonly sidebarCollapsed = signal(readSidebarCollapsed());
  protected readonly sidebarOpen = signal(false);
  /** The token is valid but e-Portal never created the account: show the stop page, not the shell. */
  protected readonly accountNotProvisioned = signal(false);
  /** Keycloak username for the stop page (name/email are the fallbacks). */
  protected readonly oidcUsername = signal<string | null>(null);
  protected readonly chatUnreadCount = signal(0);
  protected readonly fullScreenRoute = signal(false);
  /**
   * "บันทึกซื้อ-ขาย" highlights on every /trade page except the officer report, which is its own
   * menu entry under the same prefix (routerLinkActive would light both up).
   */
  protected readonly tradeMenuActive = signal(false);
  protected readonly fullMainRoute = signal(false);
  protected oidcSecurityService = inject(OidcSecurityService);
  private readonly http = inject(HttpClient);
  protected readonly oidcUserName = signal<string | null>(null);
  protected readonly oidcEmail = signal<string | null>(null);
  protected readonly oidcAvatar = signal<string | null>(null);

  protected get userName(): string {
    const profile = this.currentUserContext.profile();
    const backendName = profile?.fullName?.trim() ||
      [profile?.prefixName, profile?.firstName, profile?.middleName, profile?.lastName].filter(Boolean).join(' ').trim() ||
      profile?.username?.trim();
    return backendName || this.oidcUserName() || '';
  }

  protected get userEmail(): string {
    return this.currentUserContext.profile()?.email?.trim() || this.oidcEmail() || '';
  }

  protected readonly userAvatar = computed<string | null>(() =>
    this.currentUserContext.profile()?.profileImageUrl || this.oidcAvatar() || null
  );
  protected router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authService = inject(AuthService);
  private readonly currentUserContext = inject(CurrentUserContextService);
  private readonly notificationService = inject(NotificationService);
  private readonly chatPresence = inject(ChatPresenceService);
  protected readonly roleInvitationService = inject(RoleInvitationService);
  protected readonly pendingInvitationCount = this.roleInvitationService.pendingCount;
  private readonly tradeStore = inject(TradeStore);

  private readonly userRoles = toSignal(
    this.oidcSecurityService.userData$.pipe(
      filter(e => !!e?.userData),
      map(u => normalizeUserRoles([
        ...(u.userData?.resource_access?.[environment.clientId]?.roles ?? []),
        ...(u.userData?.realm_access?.roles ?? []),
      ]))
    ),
    {initialValue: [] as UserRole[]}
  );

  protected readonly isStaffRole = computed(() => {
    const r = this.userRoles();
    return STAFF_ROLES.some(role => r.includes(role));
  });

  protected readonly profileIncomplete = computed(() =>
    !this.isStaffRole() && this.currentUserContext.profile()?.profileCompleted === false
  );

  protected readonly isFarmer = computed(() => this.currentUserContext.hasUserRole('FARMER'));

  /** Effective role set for UI gating (token roles + delegated authorities). */
  private readonly effectiveRoles = () => this.authService.effectiveRoles();

  /**
   * FARMER/MIDDLEMAN/AGENT_OPERATOR/EXPORTER skip sidebar role locking and
   * always see every menu entry. Route guards (menuRoleGuard) still enforce
   * page access.
   */
  protected readonly hasUnlockedSidebarRole = computed(() =>
    hasSidebarUnlockedRole(this.effectiveRoles()));

  /** Sidebar visibility per the role matrix (MENU_ROLE_SETS). */
  protected readonly canSeeDashboard = computed(() =>
    this.hasUnlockedSidebarRole() || MENU_ROLE_SETS.dashboard.some(r => this.effectiveRoles().includes(r)));
  protected readonly canSeeProfile = computed(() =>
    this.hasUnlockedSidebarRole() || MENU_ROLE_SETS.profile.some(r => this.effectiveRoles().includes(r)));
  protected readonly canSeePurchaseSale = computed(() =>
    this.hasUnlockedSidebarRole() || MENU_ROLE_SETS.purchaseSale.some(r => this.effectiveRoles().includes(r)));
  protected readonly canSeeQrTrace = computed(() =>
    this.hasUnlockedSidebarRole() || MENU_ROLE_SETS.qrTrace.some(r => this.effectiveRoles().includes(r)));
  protected readonly canSeeChat = computed(() =>
    this.hasUnlockedSidebarRole() || MENU_ROLE_SETS.chat.some(r => this.effectiveRoles().includes(r)));
  protected readonly canSeeSurvey = computed(() =>
    this.hasUnlockedSidebarRole() || MENU_ROLE_SETS.survey.some(r => this.effectiveRoles().includes(r)));
  /**
   * รายงานข้อสังเกต is officers-only: the business roles' sidebar unlock must not open it
   * (a MIDDLEMAN was seeing the entry, work item 184 follow-up), so only the role set counts.
   */
  protected readonly canSeeObservations = computed(() =>
    MENU_ROLE_SETS.observations.some(r => this.effectiveRoles().includes(r)));
  /** ตั้งค่าประเภทธุรกิจ is administrators-only, so (like observations) only the role set counts. */
  protected readonly canSeeBusinessTypeSettings = computed(() =>
    MENU_ROLE_SETS.businessTypeSettings.some(r => this.effectiveRoles().includes(r)));

  protected get userRole(): string {
    const profile = this.currentUserContext.profile();
    const summary = formatUserRoleSummary(profile?.userRoles);
    if (summary) {
      return summary;
    }
    return formatUserRoleSummary(this.userRoles());
  }

  /** Business-type titles across my businesses (top 3 + ellipsis), or ''. */
  protected readonly businessTypeSummary = computed(() =>
    formatBusinessTypeSummary(
      this.tradeStore.businesses().flatMap((b) => b.businessTypeTitles ?? []),
    ));

  private readonly mobileQuery = window.matchMedia('(max-width: 768px)');
  protected readonly isMobile = signal(this.mobileQuery.matches);
  private readonly mobileQueryListener = (e: MediaQueryListEvent) => this.isMobile.set(e.matches);

  constructor() {
    this.oidcSecurityService.getUserData()
      .subscribe(userData => {
        if (userData) {
          this.oidcUsername.set(userData.preferred_username || null);
          this.oidcUserName.set(userData.name || null);
          this.oidcEmail.set(userData.email || null);
          this.oidcAvatar.set(userData.picture || userData.avatar || null);
        }
      });
    this.logAccessTokenDebug();
  }

  private logAccessTokenDebug(): void {
    if (environment.production) {
      return;
    }
    forkJoin({
      accessToken: this.oidcSecurityService.getAccessToken().pipe(take(1)),
      payload: this.oidcSecurityService.getPayloadFromAccessToken().pipe(take(1)),
    }).subscribe(({accessToken, payload}) => {
      console.log('[DEBUG] access token:', accessToken);
      console.log('[DEBUG] access token payload:', JSON.stringify(payload, null, 2));
    });
  }

  ngOnInit(): void {
    // Load the profile (with delegated authorities) first so the acting-as
    // banner, role checks and child features all see it before other init work.
    this.authService.loadUserProfile().subscribe({
      error: (error) => this.accountNotProvisioned.set(isAccountNotProvisioned(error))
    });
    // Restore the persisted "act on behalf of" selection globally so the
    // red band and harvest lists are correct on every route after refresh.
    this.tradeStore.restoreActiveAssignment();
    // The sidebar business-type summary needs the list; load once if no
    // trade page has loaded it yet.
    if (!this.tradeStore.businessesLoaded() && !this.tradeStore.loading()) {
      void this.tradeStore.loadBusinesses();
    }
    this.mobileQuery.addEventListener('change', this.mobileQueryListener);
    this.syncRouteLayoutState();
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.syncRouteLayoutState());

    // Long-lived presence channel: open connection = online (replaces the
    // heartbeat POST) and it pushes chat unread counts in real time.
    this.chatPresence.connect();
    this.chatPresence.onUnreadCount((count) => this.chatUnreadCount.set(count));

    // Header badge polling (fallback + refresh): fast while the tab is
    // visible, slow while it is hidden, immediate catch-up when it returns.
    this.documentVisibilityListener = () => this.scheduleHeaderRefresh();
    document.addEventListener('visibilitychange', this.documentVisibilityListener);
    this.refreshHeaderOnce();
    this.scheduleHeaderRefresh();
  }

  ngOnDestroy(): void {
    this.mobileQuery.removeEventListener('change', this.mobileQueryListener);
    document.removeEventListener('visibilitychange', this.documentVisibilityListener);
    this.cancelHeaderRefreshTimer();
  }

  private documentVisibilityListener: (() => void) | null = null;
  private headerRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  private headerRefreshInFlight = false;

  private refreshHeaderOnce(): void {
    if (this.headerRefreshInFlight) {
      return;
    }
    this.headerRefreshInFlight = true;
    this.refreshHeaderState().pipe(
      finalize(() => {
        this.headerRefreshInFlight = false;
      }),
    ).subscribe();
  }

  private scheduleHeaderRefresh(): void {
    this.cancelHeaderRefreshTimer();
    const interval = document.hidden
      ? AdminLayout.HEADER_REFRESH_INTERVAL_HIDDEN_MS
      : AdminLayout.HEADER_REFRESH_INTERVAL_MS;
    this.headerRefreshTimer = setTimeout(() => {
      this.headerRefreshTimer = null;
      this.refreshHeaderOnce();
      this.scheduleHeaderRefresh();
    }, interval);
  }

  private cancelHeaderRefreshTimer(): void {
    if (this.headerRefreshTimer !== null) {
      clearTimeout(this.headerRefreshTimer);
      this.headerRefreshTimer = null;
    }
  }

  toggleSidebar(): void {
    this.sidebarOpen.update(open => !open);
  }

  closeSidebar(): void {
    this.sidebarOpen.set(false);
  }

  toggleSidebarCollapse(): void {
    this.sidebarCollapsed.update(collapsed => !collapsed);
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(this.sidebarCollapsed()));
    } catch {
      // storage blocked (private mode etc.) — the toggle still works for this page load
    }
  }

  /** Tooltip for a nav item: only when the sidebar is folded and its label is hidden. */
  protected navTitle(label: string): string | null {
    return this.sidebarCollapsed() ? label : null;
  }

  logout(): void {
    this.authSession.logout();
  }

  navigateToOidcAccountInfo(): void {
    window.location.href = `${environment.accountInfoUrl}?referrer=${environment.clientId}`;
  }

  protected readonly environment = environment;

  protected goToPortalSystem(): void {
    window.location.href = `${environment.portalUrl}?referrer=${environment.clientId}`;
  }

  private refreshHeaderState(): Observable<void> {
    const requests: Observable<unknown>[] = [
      this.notificationService.refreshUnreadCount(),
      this.roleInvitationService.refreshPendingCount().pipe(
        catchError(() => of({count: 0})),
      ),
    ];
    // While the presence socket is open it owns presence + unread pushes; fall
    // back to the REST endpoints only while the socket is down.
    if (!this.chatPresence.connected()) {
      requests.push(
        this.http.post<void>(`${environment.serverUrl}/api/chat/presence/heartbeat`, null).pipe(
          catchError(() => of(undefined)),
        ),
        this.refreshChatUnreadCount(),
      );
    }
    return forkJoin(requests).pipe(map(() => undefined));
  }

  private refreshChatUnreadCount(): Observable<void> {
    return this.http.get<number>(`${environment.serverUrl}/api/chat/unread-count`).pipe(
      map((count) => {
        this.chatUnreadCount.set(count);
      }),
      catchError(() => {
        this.chatUnreadCount.set(0);
        return of(undefined);
      }),
    );
  }

  private syncRouteLayoutState(): void {
    let currentRoute: ActivatedRoute | null = this.route;
    while (currentRoute?.firstChild) {
      currentRoute = currentRoute.firstChild;
    }
    this.fullScreenRoute.set(!!currentRoute?.snapshot.data?.['fullScreen']);
    this.fullMainRoute.set(!!currentRoute?.snapshot.data?.['fullMain']);
    const path = this.router.url.split(/[?#]/)[0];
    this.tradeMenuActive.set((path === '/trade' || path.startsWith('/trade/')) && !path.startsWith('/trade/observations'));
  }
}
